import React from "react";
import { motion } from "framer-motion";
import image550 from "../assets/image550.png";

const MedOps = () => {
  return (
    <div className="min-h-screen w-full font-sans overflow-x-hidden">
      {/* Hero Section */}
      <section className="bg-[#d2e5e4] text-[#3d302c] h-[60vh] md:h-[48vh] flex flex-col justify-center items-center text-center px-4">
        <motion.h1
          className="text-4xl sm:text-5xl md:text-7xl font-bold mb-4"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: false, amount: 0.5 }}
        >
          MEDOPS
        </motion.h1>

        <motion.p
          className="text-base sm:text-lg md:text-xl font-medium max-w-xs sm:max-w-xl md:max-w-2xl"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: false, amount: 0.5 }}
        >
          Empowering Healthcare with <br className="hidden sm:block" />{" "}
          Real-Time Documentation & AI Intelligence
        </motion.p>
      </section>

      {/* Info Section */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          {/* Text from left */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.2 }}
            className="md:w-1/2 w-full"
          >
            <h2 className="text-3xl font-semibold text-black mb-4">
              Welcome to MedOps – Revolutionizing Patient Care
            </h2>
            <p className="text-gray-700 text-lg md:text-xl leading-relaxed mb-4">
              MedOps is a cutting-edge solution designed exclusively for
              hospitals to streamline patient management and enhance
              doctor-patient interactions through a seamless mobile experience.
              With our innovative platform, doctors can:
            </p>
            <ul className="list-disc list-inside text-gray-700 text-lg md:text-xl mb-4">
              <li>Instantly access patients' records anytime, anywhere.</li>
              <li>Review patient conditions and medical history with ease.</li>
              <li>
                Suggest tests and medications using AI-powered recommendations.
              </li>
              <li>
                Generate digital prescriptions, eliminating paper-based methods
                for faster and more accurate treatment.
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed text-lg md:text-xl">
              MedOps is a next-generation hospital management solution designed
              to streamline patient care and enhance doctor-patient interactions
              through an intuitive mobile application.
              <br />
              <br />
              Experience the future of patient care with MedOps – where
              efficiency meets innovation!
            </p>
          </motion.div>

          {/* Image from right */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.2 }}
            className="md:w-1/2 w-full flex justify-center"
          >
            <img
              src={image550}
              alt="MedOps hospital solution"
              className="rounded-xl shadow-md w-full max-w-xs sm:max-w-md"
            />
          </motion.div>
        </div>
      </section>
      {/* Smart Tools */}
      <section className="bg-[#d2e5e4] py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-4xl font-bold text-center text-[#3d302c] mb-12"
          >
            Revolutionizing Hospital Operations With Smart Tools
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Smart Scheduling",
                desc: "Optimize staff shifts and reduce workload imbalance using intelligent scheduling algorithms.",
              },
              {
                title: "Integrated Diagnostics",
                desc: "Connect with lab systems and imaging tools for quick results access.",
              },
              {
                title: "Real-time Monitoring",
                desc: "Monitor vitals and critical updates in real-time from a centralized dashboard.",
              },
              {
                title: "Cloud Based Records",
                desc: "Access medical history securely from anywhere with encrypted cloud support.",
              },
              {
                title: "Voice Notes",
                desc: "Doctors can record notes instantly, improving documentation.",
              },
              {
                title: "AI Decision Support",
                desc: "Access medical history securely from anywhere with encrypted cloud support.",
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false, amount: 0.3 }}
                className="group bg-white hover:bg-[#3d302c] transition-colors duration-300 rounded-xl shadow-md p-6 text-center flex flex-col justify-center items-center h-[200px] sm:h-[220px]"
              >
                <h3 className="text-xl font-semibold text-[#3d302c] mb-2 group-hover:text-white transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-black text-base group-hover:text-white transition-colors duration-300">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-white text-[#3d302c] px-4 sm:px-6 md:px-12 py-20 flex justify-center items-center">
        <div className="max-w-6xl w-full">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.3 }}
            className="text-4xl font-bold text-center mb-14 tracking-wide"
          >
            Your Health, Our Priority
          </motion.h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: false, amount: 0.3 }}
              className="bg-[#f5f5f5] rounded-xl p-6 sm:p-8 shadow-md"
            >
              <form className="space-y-6">
                <input
                  type="text"
                  placeholder="Full Name"
                  className="w-full border border-gray-300 p-4 rounded-md focus:outline-none"
                />
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full border border-gray-300 p-4 rounded-md focus:outline-none"
                />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="w-full border border-gray-300 p-4 rounded-md focus:outline-none"
                />
                <textarea
                  placeholder="Message"
                  rows="4"
                  className="w-full border border-gray-300 p-4 rounded-md resize-none focus:outline-none"
                ></textarea>
                <button
                  type="submit"
                  className="w-full bg-[#3d302c] text-white py-3 text-lg font-semibold rounded-md hover:bg-[#2e221e] transition duration-300"
                >
                  Book an Appointment
                </button>
              </form>
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: false, amount: 0.3 }}
              className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm"
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-1">📞 Call Us</h3>
                  <p className="text-lg font-light">9888268456</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">📧 Email</h3>
                  <p className="text-lg font-light">Sales@medimind.in</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">🕒 Timings</h3>
                  <div className="bg-[#4b9daf] text-white rounded-md px-4 py-4 mt-2 text-lg font-medium">
                    <p>Monday to Friday</p>
                    <p className="text-2xl font-bold mt-1">
                      9:00 <span className="text-sm font-light">AM</span> - 7:00{" "}
                      <span className="text-sm font-light">PM</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MedOps;
